// Feature 19 — Optional Device Location Record.
// A single opt-in, browser-Geolocation-only location record. Default OFF. We request permission
// at most once per session, freeze the successful snapshot and reuse it for every capture in the
// workflow (no continuous tracking). Denial / timeout / unavailable NEVER blocks capture and is
// not re-prompted for the rest of the session. Coordinates are private (owner-only) provenance.

const STORAGE_KEY = "ts_location_record_enabled";

let deniedThisSession = false; // suppress re-prompts after denial/timeout/unavailable (session-aware)
const listeners = new Set();

export function isLocationRecordEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setLocationRecordEnabled(on) {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (on) {
    // Re-enabling clears a prior denial so the user can grant permission again.
    deniedThisSession = false;
  }
  listeners.forEach((fn) => {
    try { fn(on); } catch { /* noop */ }
  });
}

export function subscribeLocationRecord(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Collect a FRESH { source, latitude, longitude, accuracy_m, recorded_at } snapshot for the
// capture workflow that is beginning now. Coordinates are NEVER cached across workflows — each
// call collects a new fix (maximumAge:0). The opt-in preference and the session-level
// permission-denied state ARE remembered so we do not re-prompt after a denial. The resulting
// snapshot is frozen by the caller onto the evidence record and reused verbatim on queue
// retries/reloads (which never call this again). Never rejects, never throws, never blocks capture.
export function getDeviceLocationSnapshot() {
  return new Promise((resolve) => {
    if (!isLocationRecordEnabled()) return resolve(null);
    if (deniedThisSession) return resolve(null); // denied this session — do not auto-request again
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);
    let settled = false;
    const done = (val) => { if (!settled) { settled = true; resolve(val); } };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = pos.coords || {};
          if (c.latitude == null || c.longitude == null) { deniedThisSession = true; return done(null); }
          done({
            source: "browser_geolocation",
            latitude: Number(c.latitude.toFixed(6)),
            longitude: Number(c.longitude.toFixed(6)),
            accuracy_m: c.accuracy != null ? Math.round(c.accuracy) : null,
            recorded_at: new Date(pos.timestamp || Date.now()).toISOString(),
          });
        },
        () => { deniedThisSession = true; done(null); }, // denied / unavailable — do not re-prompt
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }, // maximumAge:0 => always a fresh fix
      );
    } catch {
      deniedThisSession = true;
      done(null);
    }
  });
}

// Factual, owner-only display strings (mandated wording; never claim GPS/exact/verified/proven).
export function deviceLocationLines(dl) {
  if (!dl) return [];
  const lines = ["Device location recorded"];
  if (dl.accuracy_m != null) lines.push(`Reported accuracy: approximately \u00B1${Math.round(dl.accuracy_m)} m`);
  lines.push("Location supplied by your device");
  return lines;
}
